/*
  Copyright Epic Games, Inc. All Rights Reserved.
*/

import { getEnvVarOptional, COSMOS_SITE_BASE_PATH } from "@cosmos/common-utils";

import type { MetadataRoute } from "next";

const baseRobotsRules: MetadataRoute.Robots["rules"] = {
	userAgent: "*",
	disallow: [
		//root no locale paths to disallow
		"/portal*",
		"/register*",
		"/account*",
		"/dashboard*",
		"/id*",
		"/v1/developers*",
		"/internal/*",

		//locale based paths to disallow
		"/*/try-free*",
		"/*/search",

		//disallow urls that have params
		"/*?tags=",
		"/*&tags=",
		"/*?filter=",
		"/*&filter=",
		"/*?lang=",
		"/*&lang=",
		"/*?keyword=",
		"/*&keyword=",
		"/*?skip=",
		"/*&skip=",
	],
};

// Lazily build the full site URL to avoid calling getEnvVarRequired at module evaluation time.
// This module can be pulled into client bundles via barrel exports, where EPIC_SERVER_URL is unavailable.
function getSitemapUrl(): string {
	return `${getEnvVarOptional("EPIC_SERVER_URL") ?? ""}${COSMOS_SITE_BASE_PATH()}/sitemap.xml`;
}

/**
 * Base robots config used by all websites.
 * Uses a getter for `sitemap` to defer URL resolution until access time,
 * avoiding module-level evaluation of server-only env vars in client bundles.
 */
export const baseRobotsConfig: MetadataRoute.Robots = {
	rules: baseRobotsRules,
	get sitemap() {
		return getSitemapUrl();
	},
};

export function createRobotsConfig(additionalSitemaps: string[] = []): MetadataRoute.Robots {
	const sitemaps = [getSitemapUrl(), ...additionalSitemaps];

	return {
		rules: baseRobotsRules,
		sitemap: sitemaps.length === 1 ? sitemaps[0] : sitemaps,
	};
}

export default function robots(): MetadataRoute.Robots {
	return {
		rules: baseRobotsRules,
		sitemap: getSitemapUrl(),
	};
}
